<template>
  <div class="business-photos-container">
    <h6 class="section-title mb-3">Business Photos</h6>
    <p class="text-muted mb-3">Upload up to 3 photos of your business location, equipment, or facilities. These will be displayed to potential customers.</p>
    
    <!-- Photo Upload Slots -->
    <div class="row">
      <div v-for="(photo, index) in photoSlots" :key="index" class="col-md-4 mb-3">
        <div class="photo-slot">
          <!-- Existing Photo Display -->
          <div v-if="photo.url" class="photo-display">
            <img :src="photo.url" :alt="`Business Photo ${index + 1}`" class="business-photo-thumbnail">
            <div class="photo-overlay">
              <button 
                type="button" 
                class="btn btn-sm btn-danger photo-delete-btn"
                @click="confirmDeletePhoto(index)"
                :disabled="isUploading"
              >
                <i class="fas fa-trash"></i>
              </button>
              <button 
                type="button" 
                class="btn btn-sm btn-primary photo-preview-btn"
                @click="previewPhoto(photo)"
              >
                <i class="fas fa-eye"></i>
              </button>
            </div>
          </div>
          
          <!-- Upload Area -->
          <div v-else class="photo-upload-area" @click="triggerFileInput(index)">
            <div class="upload-content">
              <i class="fas fa-camera upload-icon"></i>
              <p class="upload-text">Add Photo {{ index + 1 }}</p>
              <p class="upload-subtext">Click to upload</p>
            </div>
            <input 
              :ref="`fileInput${index}`"
              type="file" 
              class="file-input" 
              accept="image/*"
              @change="handleFileSelection($event, index)"
              :disabled="isUploading"
            >
          </div>
          
          <!-- Loading State -->
          <div v-if="photo.uploading" class="upload-progress">
            <div class="spinner-border spinner-border-sm" role="status">
              <span class="visually-hidden">Uploading...</span>
            </div>
            <p class="upload-progress-text">Uploading...</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Upload Progress -->
    <div v-if="isUploading" class="alert alert-info mt-3">
      <i class="fas fa-info-circle me-2"></i>
      Uploading photos... Please wait.
    </div>
    
    <!-- Success Message -->
    <div v-if="showSuccessMessage" class="alert alert-success mt-3">
      <i class="fas fa-check-circle me-2"></i>
      Photos uploaded successfully!
    </div>
    
    <!-- Error Message -->
    <div v-if="uploadError" class="alert alert-danger mt-3">
      <i class="fas fa-exclamation-circle me-2"></i>
      {{ uploadError }}
    </div>
  </div>
  
  <!-- Photo Preview Modal -->
  <div v-if="previewModal.show" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Business Photo Preview</h5>
          <button type="button" class="btn-close" @click="closePreview"></button>
        </div>
        <div class="modal-body text-center">
          <img :src="previewModal.photo.url" :alt="previewModal.photo.name" class="img-fluid">
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closePreview">Close</button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Delete Confirmation Modal -->
  <div v-if="deleteModal.show" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-sm">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Delete Photo</h5>
          <button type="button" class="btn-close" @click="closeDeleteModal"></button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete this business photo?</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-danger" @click="deletePhoto" :disabled="isUploading">Delete</button>
          <button type="button" class="btn btn-secondary" @click="closeDeleteModal">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { locationStore } from '@/stores/location'
import { authStore } from '@/stores/auth'
import axios from 'axios'

export default {
  name: 'BusinessPhotoUpload',
  props: {
    firm: Object
  },
  emits: ['changed'],
  data() {
    return {
      maxPhotos: 3,
      isUploading: false,
      uploadError: null,
      showSuccessMessage: false,
      photoSlots: [
        { url: null, name: null, uploading: false },
        { url: null, name: null, uploading: false },
        { url: null, name: null, uploading: false }
      ],
      previewModal: {
        show: false,
        photo: null
      },
      deleteModal: {
        show: false,
        photoIndex: null
      }
    }
  },
  computed: {
    ...mapState(authStore, ['token']),
    ...mapState(locationStore, ['profileData', 'location']),
    launchpadFirmId() {
      return this.location?.firmId || this.launchpadFirmId
    },
    apiUrl() {
      return window.config ? window.config.apiUrl : ''
    }
  },
  watch: {
    'profileData.businessPhotos': {
      handler(newPhotos) {
        console.log('BusinessPhotos watcher triggered:', newPhotos)
        console.log('ProfileData full object:', this.profileData)
        
        if (newPhotos && Array.isArray(newPhotos)) {
          console.log('Loading saved business photos:', newPhotos)
          // Initialize photo slots with saved photos
          for (let i = 0; i < this.maxPhotos; i++) {
            if (newPhotos[i]) {
              this.photoSlots[i] = { 
                url: newPhotos[i].url, 
                name: newPhotos[i].name,
                uploading: false 
              }
              console.log(`Restored photo ${i}:`, this.photoSlots[i])
            } else {
              this.photoSlots[i] = { url: null, name: null, uploading: false }
            }
          }
        } else {
          console.log('No business photos found or not an array')
        }
      },
      immediate: true
    },
    profileData: {
      handler(newData) {
        console.log('ProfileData changed:', newData)
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...mapActions(locationStore, ['updateProfileField', 'saveProfileData']),
    triggerFileInput(index) {
      if (this.isUploading) return
      this.$refs[`fileInput${index}`][0].click()
    },
    async handleFileSelection(event, index) {
      const file = event.target.files[0]
      if (!file) return
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.uploadError = 'Please select an image file (JPG, PNG, GIF, etc.)'
        return
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.uploadError = 'Image file must be smaller than 10MB'
        return
      }
      
      await this.uploadPhoto(file, index)
      
      // Clear the input
      event.target.value = ''
    },
    async uploadPhoto(file, index) {
      this.uploadError = null
      this.showSuccessMessage = false
      this.photoSlots[index].uploading = true
      this.isUploading = true
      
      try {
        // Debug logging
        console.log('Upload attempt:', {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          firmId: this.launchpadFirmId,
          token: this.token ? 'Present' : 'Missing',
          apiUrl: this.apiUrl
        })
        
        // Using FormData approach like the working blog-attachment component
        const formData = new FormData()
        formData.append('files', file, file.name)
        formData.append('folder', 'clientfiles')
        formData.append('firm', this.launchpadFirmId.toString())
        formData.append('id', this.launchpadFirmId.toString())
        formData.append('vin', '')
        formData.append('name', '')
        
        // Debug FormData contents
        for (let [key, value] of formData.entries()) {
          console.log('FormData entry:', key, value)
        }
        
        const response = await axios({
          method: 'post',
          url: `${this.apiUrl}/data/uploadFiles`,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${this.token}`
          }
        })
        
        console.log('Upload response:', response)
        
        // Use the URL returned by the backend (actual Azure Blob Storage URL)
        const photoUrl = response.data || `${window.config.storageUrl || this.apiUrl}/clientfiles/${this.launchpadFirmId}/${file.name}`
        
        this.photoSlots[index] = {
          url: photoUrl,
          name: file.name,
          uploading: false
        }
        
        // Update the profile data
        this.savePhotosToProfile()
        
        // Save the profile data to the backend immediately
        await this.saveProfileData(this.launchpadFirmId)
        
        this.showSuccessMessage = true
        this.$emit('changed')
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          this.showSuccessMessage = false
        }, 3000)
        
      } catch (error) {
        console.error('Photo upload error:', error)
        console.error('Error response:', error.response)
        console.error('Error response data:', error.response?.data)
        console.error('Error status:', error.response?.status)
        console.error('Error headers:', error.response?.headers)
        
        // Try to get the actual error message from the backend
        let errorMessage = 'Failed to upload photo. Please try again.'
        if (error.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message
          } else {
            errorMessage = JSON.stringify(error.response.data)
          }
        }
        
        this.uploadError = errorMessage
        this.photoSlots[index].uploading = false
      } finally {
        this.isUploading = false
      }
    },
    savePhotosToProfile() {
      const businessPhotos = this.photoSlots
        .filter(slot => slot.url)
        .map(slot => ({
          url: slot.url,
          name: slot.name
        }))
      
      this.updateProfileField('businessPhotos', businessPhotos, this.launchpadFirmId)
    },
    confirmDeletePhoto(index) {
      this.deleteModal.photoIndex = index
      this.deleteModal.show = true
    },
    async deletePhoto() {
      const index = this.deleteModal.photoIndex
      if (index === null || index === undefined) return
      
      this.isUploading = true
      this.uploadError = null
      
      try {
        // Call delete API if needed
        const photo = this.photoSlots[index]
        if (photo.name) {
          await axios.delete(`${this.apiUrl}/data/removeFile`, {
            params: {
              folder: 'clientfiles',
              filename: photo.name,
              id: this.launchpadFirmId
            },
            headers: {
              Authorization: `Bearer ${this.token}`
            }
          })
        }
        
        // Clear the photo slot
        this.photoSlots[index] = { url: null, name: null, uploading: false }
        
        // Update profile data
        this.savePhotosToProfile()
        
        // Save the profile data to the backend immediately
        await this.saveProfileData(this.launchpadFirmId)
        
        this.closeDeleteModal()
        this.$emit('changed')
        
      } catch (error) {
        console.error('Photo deletion error:', error)
        this.uploadError = 'Failed to delete photo. Please try again.'
      } finally {
        this.isUploading = false
      }
    },
    previewPhoto(photo) {
      this.previewModal.photo = photo
      this.previewModal.show = true
    },
    closePreview() {
      this.previewModal.show = false
      this.previewModal.photo = null
    },
    closeDeleteModal() {
      this.deleteModal.show = false
      this.deleteModal.photoIndex = null
    }
  }
}
</script>

<style lang="scss" scoped>
.business-photos-container {
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
}

.section-title {
  color: #495057;
  font-weight: 600;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.photo-slot {
  position: relative;
  height: 200px;
  border: 2px dashed #dee2e6;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #80bdff;
  }
}

.photo-display {
  position: relative;
  height: 100%;
  
  .business-photo-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .photo-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    opacity: 0;
    transition: opacity 0.3s ease;
    
    &:hover {
      opacity: 1;
    }
  }
  
  .photo-delete-btn,
  .photo-preview-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.photo-upload-area {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #ffffff;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  .upload-content {
    text-align: center;
    
    .upload-icon {
      font-size: 2rem;
      color: #6c757d;
      margin-bottom: 0.5rem;
    }
    
    .upload-text {
      font-weight: 600;
      color: #495057;
      margin-bottom: 0.25rem;
    }
    
    .upload-subtext {
      font-size: 0.875rem;
      color: #6c757d;
      margin-bottom: 0;
    }
  }
}

.file-input {
  display: none;
}

.upload-progress {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  
  .upload-progress-text {
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #495057;
  }
}

.photo-label {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  text-align: center;
}

.alert {
  border-radius: 0.5rem;
  
  i {
    font-size: 1rem;
  }
}

// Modal styles
.modal {
  z-index: 1050;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  opacity: 0.5;
  
  &:hover {
    opacity: 0.75;
  }
}

.text-muted {
  color: #6c757d !important;
}
</style>